import { useSelector } from "store/useSelector";
import { useDispatch } from "react-redux";
import {
  DATA_DUMP_AGGREGATE_QUERY,
  midgardClients,
} from "../../providers/MidgardProvider";
import {
  DATA_DUMP_TYPES,
  UPDATE_LOT_START_DATA_DUMP_FOR_PROCESSES,
} from "../../providers/GlobalStateProvider";
import type { TimeRangeFinite } from "../../types/types";
import { useEffect, useState } from "react";
import { isEqual } from "lodash";
import {
  useGetPageFromPath,
  useFocusRange,
  useUnmount,
} from "../../utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useCleanRawDataDump } from "./dataDumpGetterUtils";
import { ONE_DAY_OF_MS, ONE_HOUR_OF_MS } from "../../utils/timeUtils";
import { CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING } from "../../constants/customers";
import {
  DEFAULT_DATA_DUMP_UPDATE_INTERVAL,
  FAST_RETRY_INTERVAL,
} from "../../constants/intervals";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import useGetIsMounted from "utils/useGetIsMounted";
import useQueryWithErrorHandling from "utils/useQuery";
import { FETCH_POLICY } from "segments/AggregatesGetter/aggregatesGetterUtils";
import { getIsCypressRunning } from "constants/constants";
import {
  useForceRerenderWhenRetrotagChanges,
  useJustRetrotagged,
} from "components/Dashboard/Timeline/RetrotagPopper/retrotagQueryHooks";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { useInterval } from "react-use";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useFocusRangeFromQuery } from "reports/components/Reports/reportUtils";
import { ONE_SECOND_OF_MS } from "reports/utils/timeUtils";

const isCurrentlyTheWeekend =
  new Date().getDay() === 0 || new Date().getDay() === 6;

export const MAX_DATA_DUMP_TIME_RANGE_MS =
  1 *
  ONE_DAY_OF_MS *
  // ravenjest weekend shifts are 2 days long; we need to fetch 2 days of data if test is running during the weekend
  (getIsCypressRunning() && isCurrentlyTheWeekend ? 2 : 1);
export const LOT_PROGRESS_DATA_DUMP_TIME_RANGE_MS = 10 * ONE_DAY_OF_MS;

type DataDumpGetterProps = {
  timeRange: TimeRangeFinite | null;
  processIds: string[];
  initialPollInterval?: number;
  initialDelayMs?: number;
  dataDumpAggType: keyof typeof DATA_DUMP_TYPES;
  additionalPayload?: Record<string, unknown>;
  customOnCompleted?: (data: any) => void;
  customAfterCompleted?: (data: any) => void;
  /** ms interval for retrying query if data hasn't returned by then */
  retryUntilReceivedInterval?: number;
};

/** fetches Midgard's DataDumpAggregate and stores it in the GlobalStateProvider store
 *
 * pass any additional payload data needed in the reducer action,
 * and the dataDumpType corresponding to the store's reducer action
 */
const DataDumpGetter = ({
  timeRange,
  processIds,
  additionalPayload = {},
  initialPollInterval = DEFAULT_DATA_DUMP_UPDATE_INTERVAL,
  dataDumpAggType,
  customOnCompleted,
  customAfterCompleted,
  retryUntilReceivedInterval,
  initialDelayMs = 0,
}: DataDumpGetterProps) => {
  const shouldSkipQuery = useShouldSkipQuery({
    timeRange,
    processIds,
    dataDumpAggType,
  });
  const rerenderKey = useForceRerenderWhenRetrotagChanges();

  const [initialDelayExpired, setInitialDelayExpired] = useState(false);
  const [retryKey, setRetryKey] = useState(Math.random());
  const [completed, setCompleted] = useState(false);
  useInterval(() => {
    if (retryUntilReceivedInterval && !completed) {
      setRetryKey(Math.random());
    }
  }, retryUntilReceivedInterval);

  useInterval(() => {
    setInitialDelayExpired(true);
  }, initialDelayMs);

  return shouldSkipQuery || !initialDelayExpired ? null : (
    <Query
      key={`${retryKey}${rerenderKey}`}
      {...{
        timeRange,
        processIds,
        initialPollInterval,
        dataDumpAggType,
        additionalPayload,
        customOnCompleted,
        customAfterCompleted,
        setCompleted,
      }}
    />
  );
};

function Query({
  timeRange,
  processIds,
  initialPollInterval,
  dataDumpAggType,
  additionalPayload,
  customOnCompleted,
  customAfterCompleted,
  setCompleted,
}: any) {
  useEffect(() => {
    setCompleted(false);
  }, [
    timeRange,
    processIds,
    initialPollInterval,
    dataDumpAggType,
    additionalPayload,
    customOnCompleted,
    customAfterCompleted,
    setCompleted,
  ]);

  const [pollInterval, setPollInterval] = useState(initialPollInterval);
  const ckey = useCustomerKey();
  const client = midgardClients.get(ckey);
  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();
  const shouldAllocate =
    isMidgardDDAEnabled && ckey in CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING;
  const trendsPageFilterStateProcess = useSelector(
    state => state.legacy.trendsPageFilterState.process,
    isEqual
  );
  const isTrendsPageFilteredByProcess =
    trendsPageFilterStateProcess.name.toLowerCase() !== "all";
  const { isTrendsPage, isTier0Page } = useGetPageFromPath();
  const machemetryIds =
    isTrendsPage && isTrendsPageFilteredByProcess
      ? [trendsPageFilterStateProcess.id]
      : processIds;
  const dispatch = useDispatch();
  const getIsMounted = useGetIsMounted();
  const cleanRawDataDump = useCleanRawDataDump();

  // disable the DDA cache if we retrotagged recently (to prevent stale data from being displayed)
  const [justRetrotagged] = useJustRetrotagged();
  const [retrotaggedRecently, setRetrotaggedRecently] = useState(false);

  useEffect(() => {
    let timer;
    if (justRetrotagged) {
      setRetrotaggedRecently(true);
      timer = setTimeout(() => {
        setRetrotaggedRecently(false);
      }, 10 * 1000);
    }
    return () => clearTimeout(timer);
  }, [justRetrotagged]);

  // on tier0 page we cut off the DDA range at the current time & poll every 30s
  const [now, setNow] = useState(Date.now());
  useInterval(
    () => {
      setNow(Date.now());
    },
    isTier0Page ? 30 * ONE_SECOND_OF_MS : null
  );

  const { error, loading } = useQueryWithErrorHandling(
    dataDumpAggType,

    DATA_DUMP_AGGREGATE_QUERY,
    {
      // run it if the trends page is not filtered to "all" shifts
      ...(pollInterval
        ? { pollInterval: pollInterval * multiplyNetworkRequestIntervals }
        : {}),
      client,
      variables: {
        ge: timeRange ? timeRange.ge.toString() : "0",
        lt: timeRange
          ? (isTier0Page
              ? // on tier0, we report live
                Math.min(now, timeRange.lt)
              : timeRange.lt
            ).toString()
          : "0",
        machemetryIds,
        shouldAllocate,
        ...(retrotaggedRecently ? { useCache: false } : {}),
      },
      fetchPolicy: FETCH_POLICY,
      onError: err => {
        // if we get an error, try again in 10 seconds
        if (getIsMounted()) {
          setPollInterval(FAST_RETRY_INTERVAL);
          console.log(
            `Data dump query gone wrong. cKey: ${ckey}, ge: "${timeRange?.ge}", lt: "${timeRange?.lt}", machemetryIds: ${machemetryIds}`
          );
          console.log(JSON.stringify(err));
        }
      },
      onCompleted: (...args) => {
        setCompleted(true);
        if (customOnCompleted) {
          customOnCompleted(...args);
        } else {
          const data = args[0];
          const ready =
            getIsMounted() && data?.dataDumpAggregate && !loading && !error;
          if (ready) {
            const dataDumpObj = cleanRawDataDump(
              data.dataDumpAggregate,
              machemetryIds
            );
            dispatch({
              type: dataDumpAggType,
              payload: { ...additionalPayload, dataDumpObj },
            });
            setPollInterval(initialPollInterval);
          }
        }
        if (customAfterCompleted) {
          customAfterCompleted(...args);
        }
      },
    }
  );

  return null;
}

function useShouldSkipQuery({
  timeRange,
  processIds,
  dataDumpAggType,
}: {
  timeRange: TimeRangeFinite | null;
  processIds: string[];
  dataDumpAggType: string;
}) {
  const { isTrendsPage, isReportsPage, isTier0Page } = useGetPageFromPath();
  const focusRangeDashboardPages = useFocusRange();
  const focusRangeFromQuery = useFocusRangeFromQuery();
  const focusRange = isReportsPage
    ? focusRangeFromQuery
    : focusRangeDashboardPages;

  const [isMounted, setIsMounted] = useState(true);
  useUnmount(() => {
    setIsMounted(false);
  });

  const isTimeRangeWithinFocusRange =
    timeRange &&
    focusRange &&
    getIsRangeWithinFocusRange(timeRange, focusRange);

  const timeRangeDuration = timeRange
    ? timeRange.lt - (timeRange.ge || timeRange.lt) // if ge is 0, pretend time range is 0 -- then skip query because isTimeRangeZero
    : 0;

  const isTimeRangeInverted = timeRangeDuration < 0;
  const isTimeRangeZero = timeRangeDuration === 0;
  if (isTimeRangeInverted) {
    console.error(
      `DataDumpGetter time range is inverted! location.pathname: ${
        window.location.pathname
      }, timeRange: ${JSON.stringify(timeRange)} (${(
        timeRangeDuration / ONE_HOUR_OF_MS
      ).toFixed(1)} hours), (${timeRange && new Date(timeRange.ge)} — ${
        timeRange && new Date(timeRange.lt)
      }), processIds: ${JSON.stringify(processIds)}`
    );
  }

  // customers who use the lot progress bar need to fetch data back to the beginning of the lot
  const { isTimeRangeSmallEnough, maxDataDumpDays } =
    useIsTimeRangeSmallEnough(timeRangeDuration);
  const isTrendsPageFinishedInitialFilter = useSelector(
    state => state.legacy.isTrendsPageFinishedInitialFilter
  );
  if (
    !isTimeRangeSmallEnough &&
    (!isTrendsPage || isTrendsPageFinishedInitialFilter)
  ) {
    console.log(
      `Data dump time range too long, ${
        timeRangeDuration / ONE_DAY_OF_MS
      } days requested, max is ${maxDataDumpDays} day${
        maxDataDumpDays === 1 ? "" : "s"
      }`
    );
  }

  const isFocusRangeValid =
    isReportsPage ||
    isTier0Page ||
    dataDumpAggType === UPDATE_LOT_START_DATA_DUMP_FOR_PROCESSES ||
    (isTimeRangeWithinFocusRange && isTimeRangeSmallEnough);

  const isStorybook = useIsStorybook();
  const isCypressRunning = getIsCypressRunning();

  return (
    isStorybook ||
    !isMounted ||
    !timeRange ||
    !focusRange ||
    isTimeRangeInverted ||
    isTimeRangeZero ||
    // data dump since lot start can go outside of focus range
    !isFocusRangeValid ||
    // skip if we're on trends page in cypress
    (isTrendsPage && (isCypressRunning || !isTrendsPageFinishedInitialFilter))
  );
}

function getIsRangeWithinFocusRange(
  timeRange: TimeRangeFinite,
  focusRange: TimeRangeFinite
) {
  // Check within less than an hour after focusRange.lt
  // This is because some shifts don't end at the hour, so the hourly trend chart bars
  // need to span farther than the focusrange
  // E.g. shift is 7AM - 5:30PM, we need 11 bars, with the last one being 5 - 6PM
  return (
    timeRange.lt < focusRange.lt + ONE_HOUR_OF_MS &&
    timeRange.ge >= focusRange.ge
  );
}

export function useIsTimeRangeSmallEnough(timeRangeDuration: number) {
  const [
    {
      general: { enableLotInfoModule, overrideDdaLimitDays },
    },
  ] = useFlightConfig();
  const maxDataDumpDuration = overrideDdaLimitDays
    ? overrideDdaLimitDays * ONE_DAY_OF_MS
    : enableLotInfoModule
      ? LOT_PROGRESS_DATA_DUMP_TIME_RANGE_MS
      : MAX_DATA_DUMP_TIME_RANGE_MS;

  const maxDataDumpDays = maxDataDumpDuration / ONE_DAY_OF_MS;
  const isTimeRangeSmallEnough =
    timeRangeDuration <= maxDataDumpDuration ||
    window.location.pathname.includes("fromSnapshots");
  return { isTimeRangeSmallEnough, maxDataDumpDays };
}

export default DataDumpGetter;
